/**
 * Utility functions for handling image URLs
 */

/**
 * Get full image URL from relative path
 * @param {string} imagePath - Relative or absolute image path
 * @returns {string} Full image URL
 */
export const getImageUrl = (imagePath) => {
  if (!imagePath) return '';
  
  // If already a full URL, return as is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  // Get backend base URL from environment or default
  // Production: https://api.bookyatri.com/api → https://api.bookyatri.com
  // Development: http://localhost:5000/api → http://localhost:5000
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  
  // Extract backend base URL (remove /api if present)
  let BACKEND_BASE_URL;
  if (API_BASE_URL.includes('api.bookyatri.com')) {
    // Production: Use api.bookyatri.com
    BACKEND_BASE_URL = 'https://api.bookyatri.com';
  } else if (API_BASE_URL.endsWith('/api')) {
    BACKEND_BASE_URL = API_BASE_URL.slice(0, -4); // Remove '/api'
  } else if (API_BASE_URL.includes('/api')) {
    BACKEND_BASE_URL = API_BASE_URL.split('/api')[0];
  } else {
    BACKEND_BASE_URL = 'http://localhost:5000';
  }
  
  // Ensure no trailing slash
  BACKEND_BASE_URL = BACKEND_BASE_URL.replace(/\/$/, '');
  
  // If it's a relative path starting with /storage/uploads, prepend backend URL
  if (imagePath.startsWith('/storage/uploads/') || imagePath.startsWith('/storage/')) {
    return `${BACKEND_BASE_URL}${imagePath}`;
  }
  
  // If it's a relative path starting with /, prepend backend URL
  if (imagePath.startsWith('/')) {
    return `${BACKEND_BASE_URL}${imagePath}`;
  }
  
  // Otherwise return as is
  return imagePath;
};

/**
 * Get full image URLs for an array of image paths
 * @param {Array<string>} imagePaths - Array of relative or absolute image paths
 * @returns {Array<string>} Array of full image URLs
 */
export const getImageUrls = (imagePaths) => {
  if (!Array.isArray(imagePaths)) return [];
  return imagePaths.map(path => getImageUrl(path));
};

/**
 * Get full image URL from image object (with url property)
 * @param {Object} imageObj - Image object with url property
 * @returns {string} Full image URL
 */
export const getImageUrlFromObject = (imageObj) => {
  if (!imageObj) return '';
  if (typeof imageObj === 'string') return getImageUrl(imageObj);
  if (imageObj.url) return getImageUrl(imageObj.url);
  return '';
};
